import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { generalShowErrorSnackbar } from '../store/general/Functions';
import { setLocalStorageSession } from './localStorageService';
import { IUserToken } from '../@types/model/user/userToken';
import { navPath } from '../store/nav/Actions';
import { dispatch } from '../store/Index';
import { authSetSession } from '../store/auth/Actions';
import { VERSION } from '../../version';
import { IApiError } from '../@types/other';

let requestInterceptorNum = 0;
let responseInterceptorNum = 0;
export const initializeInterceptor = (token : string) => {
    axios.interceptors.request.eject(requestInterceptorNum);
    axios.interceptors.response.eject(responseInterceptorNum);

    if (!!token) {
        requestInterceptorNum = axios.interceptors.request.use((config : AxiosRequestConfig) => {
            if (!config.headers['Authorization'] || config.headers['Authorization'] === '' || config.headers['Authorization'] === 'Bearer ') {
                config.headers['Authorization'] = 'Bearer ' + token;
            }

            // Set Application Version Header
            config.headers['X-App-Version'] = VERSION.version;

            return config;
        });

        responseInterceptorNum = axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                const ppecbError = String(error?.response?.config?.url).includes('ppecb.com'); // prevents logout when getting ppecb token fails
                if (!!error.response && error.response.status === 401) {
                    if (!ppecbError) { // prevents logout when getting ppecb token fails
                        initializeInterceptor('');
                        dispatch(authSetSession(null));
                        setLocalStorageSession(null);
                    }
                    generalShowErrorSnackbar(`Unauthorised. ${error.response.data?.Message ?? error.response.data}`);
                    return Promise.reject(error.response);
                } else if (!!error.response && error.response.status === 403) {
                    navPath('/noRights');
                    generalShowErrorSnackbar(`Insufficient rights. ${error.response.data?.Message ?? error.response.data}`);
                    return Promise.reject(error.response);
                }

                if (!error || !error.response) {
                    generalShowErrorSnackbar('Connection Error.');
                    return Promise.reject();
                }

                return Promise.reject(error.response);
            });
    }
};

export const googleAuth = (idToken ?: string) : AxiosPromise<IUserToken> => axios.get(`${API_URL}/v1/Rights/Authorisation/GoogleAuth`, { params: { idToken } });
export const logout = () : AxiosPromise => axios.get(`${API_URL}/v1/Rights/Authorisation/Logout`, { params: { } });

export const logoutUsers = (userIds : Array<number>) : AxiosPromise => {
    return axios.post(`${API_URL}/v1/Rights/Authorisation/LogoutUsers`, userIds);
};

export const logInManual = (emailOrUsername : string, password : string) : AxiosPromise<IUserToken> => {
    return axios.post(`${API_URL}/v1/Rights/Authorisation/LogIn`, {
        emailOrUsername,
        password,
    });
};

export async function setEmployeeNumber(employeeNumber : string) : Promise<IUserToken | null> {
    try {
        const result = await axios.post<IUserToken | null>(`${API_URL}/v1/Rights/User/SetEmployeeNumber`, {}, {
            params : {
                employeeNumber,
            },
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        generalShowErrorSnackbar(error.data?.Message ?? 'Error setting employee nr.');
        return null;
    }
}

export async function setPassword(password : string) : Promise<IUserToken | null> {
    return await updatePassword(undefined, password);
}

export async function updatePassword(oldPassword : string | null | undefined, newPassword : string) : Promise<IUserToken | null> {
    try {
        const result = await axios.post<IUserToken | null>(`${API_URL}/v1/Rights/User/UpdatePassword`, {
            oldPassword,
            newPassword,
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message ?? '');
        } else if (!!error && error.status !== 401 && error.status !== 403)  {
            generalShowErrorSnackbar('Error setting password.');
        }
        return null;
    }
}

export async function refreshSession() : Promise<IUserToken | null> {
    try {
        const result = await axios.get<IUserToken | null>(`${API_URL}/v1/Rights/Authorisation/GetSession`, {
            params : {
            },
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message ?? '');
        } else if (!!error && error.status !== 401 && error.status !== 403)  {
            generalShowErrorSnackbar('Error refreshing session');
        }
        return null;
    }
}
